/**
 * The «نوبت حضوری» filter shows in-person booking only.
 *
 * Hamidi, 2026-10-16 (Lead again, msg 25039): «با آن که فیلتر نوبت حضوری انتخاب شده است، انگار باز
 * اولویت با ویزیت آنلاین است». The owner's reading: when the patient explicitly asks for an in-person
 * visit, the results should not keep offering online visits. On the live page
 * `/s/tehran/?text=قلب&turn_type=non-consult` every card still carried the «ویزیت آنلاین» button (first),
 * the «ویزیت آنلاین: … تومان» price line and the «آنلاین: N ساعت پیش» line, and the page showed the
 * «همین الان با پزشک متخصص گفت‌وگو کنید» banner and the promoted online-visit card.
 *
 * Ranking was fixed on the adapter side (semantic-search !218); this is what the patient sees.
 * A card keeps its online-visit button only when it would otherwise have no button at all — none did
 * on 240 cards checked on 2026-10-16, but an empty card is worse than an extra button.
 */

/** The in-person bucket of the `turn_type` filter (SEAPI facet value). */
export const IN_PERSON_TURN_TYPE = 'non-consult';

/** The title the search API gives the online-visit action — the same test the Plasmic cards use. */
export const ONLINE_VISIT_ACTION_TITLE = 'ویزیت آنلاین';

export function isInPersonView(turnType: unknown): boolean {
  return turnType === IN_PERSON_TURN_TYPE;
}

type Action = { title?: unknown };

export function isOnlineVisitAction(action: Action | null | undefined): boolean {
  return action?.title === ONLINE_VISIT_ACTION_TITLE;
}

export function hasOnlineVisitAction(actions: unknown): boolean {
  return Array.isArray(actions) && actions.some(a => isOnlineVisitAction(a as Action));
}

/**
 * The card as the in-person view shows it: no online-visit button and nothing the price line can read.
 * Returns the same object when there is nothing to remove, so memoised lists keep their identity.
 */
export function toInPersonCard<T extends { actions?: unknown }>(item: T): T {
  const actions = item?.actions;
  if (!Array.isArray(actions) || !hasOnlineVisitAction(actions)) return item;
  const inPerson = actions.filter(a => !isOnlineVisitAction(a as Action));
  if (inPerson.length === 0) return item;
  return { ...item, actions: inPerson, price: undefined, consult_services: [] };
}

export function toInPersonResults<T extends { actions?: unknown }>(items: T[]): T[] {
  let changed = false;
  const out = items.map(item => {
    const next = toInPersonCard(item);
    if (next !== item) changed = true;
    return next;
  });
  return changed ? out : items;
}
